import { StoreRegion } from '~/graphql/graphql.gen';
import { equals } from '~/utils/array';
import {
  getCountryEmoji,
  getEnglishCountryPronoun,
  getFrenchCountryPronoun,
} from '~/utils/country';

import type { CrawlingProduct } from './types';
import { StatusCodes } from './types';

const isProductVariantsCrawlable = (product: CrawlingProduct) =>
  typeof product._subRows !== 'undefined' &&
  (product._subRows ?? []).some((variant) => isUrlCrawlable(variant.url));

const isUrlCrawlable = (url: string) => url !== 'undefined' && url !== '';

export const isProductCrawlable = (product: CrawlingProduct) =>
  isUrlCrawlable(product.url) || isProductVariantsCrawlable(product);

export const formatMessageOriginCountry = (
  countryCode: string,
  storeRegion: StoreRegion,
) => {
  const regionNames = new Intl.DisplayNames(
    [storeRegion === StoreRegion.Fr ? 'fr' : 'en'],
    { type: 'region' },
  );
  const emoji = getCountryEmoji(countryCode);
  if (storeRegion === StoreRegion.Fr)
    return `${emoji ? `${emoji} ` : ''}Fabriqué ${getFrenchCountryPronoun(countryCode)} ${regionNames.of(countryCode)}.`;
  else
    return `${emoji ? `${emoji} ` : ''}Made in ${getEnglishCountryPronoun(countryCode)}${regionNames.of(countryCode)}.`;
};

export const hasSameImagesVariants = (products: CrawlingProduct[]) => {
  if (products.length === 0) return true;

  const referenceImages = products[0]?.images.toSorted() ?? [];

  if (
    products.some(
      (variant) => !equals(referenceImages, variant.images.toSorted()),
    )
  ) {
    return false;
  }

  return true;
};

// The CSV parser emits one option entry per option *column*, so an option-less
// row still carries entries with empty values. A row therefore "has options"
// only when at least one entry has a non-empty name and value.
const realOptions = (product: CrawlingProduct) =>
  (product.options ?? []).filter(
    (option) => option.name !== '' && option.value !== '',
  );

const hasOptions = (product: CrawlingProduct) =>
  realOptions(product).length > 0;

const optionColumnsKey = (product: CrawlingProduct) =>
  (product.options ?? [])
    .flatMap((option, index) =>
      option.name !== '' && option.value !== '' ? [index] : [],
    )
    .join(',');

const variantKey = (product: CrawlingProduct) =>
  realOptions(product)
    .map((option) => `${option.name} ${option.value}`)
    .join('');

/**
 * A product grouped from several CSV rows sharing the same name must be a
 * coherent set of variants: every row carries options (and their values are
 * distinct), or it is a single row with no option. This returns the offending
 * rows — each variant in error plus the parent product — so the table can flag
 * them. Keyed by object identity to match TanStack's `row.original` refs.
 */
export const getProductOptionErrors = (
  product: CrawlingProduct,
): [CrawlingProduct, StatusCodes][] => {
  const variants = product._subRows;
  if (!variants || variants.length <= 1) return [];

  const withOptions = variants.filter(hasOptions);
  const withoutOptions = variants.filter((variant) => !hasOptions(variant));
  const mixed = withOptions.length > 0 && withoutOptions.length > 0;
  const duplicateNoOption =
    withOptions.length === 0 && withoutOptions.length > 1;

  const seen = new Map<string, number>();
  const duplicateKeys = new Set<string>();
  withOptions.forEach((variant) => {
    const key = variantKey(variant);
    const count = (seen.get(key) ?? 0) + 1;
    seen.set(key, count);
    if (count > 1) duplicateKeys.add(key);
  });

  const columnsCount = new Map<string, number>();
  withOptions.forEach((variant) => {
    const key = optionColumnsKey(variant);
    columnsCount.set(key, (columnsCount.get(key) ?? 0) + 1);
  });
  const [referenceColumnsKey] = [...columnsCount.entries()].reduce(
    (best, current) => (current[1] > best[1] ? current : best),
    ['', 0],
  );
  const inconsistentColumns = columnsCount.size > 1;

  const entries: [CrawlingProduct, StatusCodes][] = [];
  variants.forEach((variant) => {
    if (!hasOptions(variant) && (mixed || duplicateNoOption)) {
      entries.push([variant, StatusCodes.CrawlingErrorMixedOptions]);
    } else if (
      hasOptions(variant) &&
      optionColumnsKey(variant) !== referenceColumnsKey
    ) {
      entries.push([
        variant,
        StatusCodes.CrawlingErrorInconsistentOptionColumns,
      ]);
    } else if (hasOptions(variant) && duplicateKeys.has(variantKey(variant))) {
      entries.push([variant, StatusCodes.CrawlingErrorDuplicateVariants]);
    }
  });

  if (entries.length > 0) {
    entries.push([
      product,
      mixed || duplicateNoOption
        ? StatusCodes.CrawlingErrorMixedOptions
        : inconsistentColumns
          ? StatusCodes.CrawlingErrorInconsistentOptionColumns
          : StatusCodes.CrawlingErrorDuplicateVariants,
    ]);
  }

  return entries;
};
